// Unifies the four month-calendar feeds (job install, estimate, scheduled
// task, warranty visit) into one discriminated-union segment list per week,
// each shaped as LaneInput (startCol/endCol) so packLanes can stack them in
// real grid rows. This replaces the old split layout where tasks/warranty
// were absolutely positioned over the job lanes (→ collisions).
//
// The COMPONENT band-packs: pack jobs/estimates first, then tasks/warranty
// offset below — packLanes sorts by (startCol,endCol) only, so merging all
// kinds into one pack would let a single-day task displace a multi-day job.
// Banding keeps jobs' layout identical to before + prioritized.

import type {
  CalendarAssignmentRow,
  CalendarTaskRow,
  CalendarWarrantyVisitRow,
} from '@gutterinv/shared';
import { splitByWeek } from './calendar-week-segments.js';

interface MonthSegmentBase {
  /** Inclusive column 0..6 within the week (0 = first day rendered). */
  startCol: number;
  endCol: number;
  /** True when this segment holds the first/last day of the full range —
   *  drives rounded-end rendering for multi-week spans. */
  isFirstSegment: boolean;
  isLastSegment: boolean;
}

export type MonthSegment =
  | (MonthSegmentBase & { kind: 'install' | 'estimate'; assignment: CalendarAssignmentRow })
  | (MonthSegmentBase & { kind: 'task'; task: CalendarTaskRow })
  | (MonthSegmentBase & { kind: 'warranty'; visit: CalendarWarrantyVisitRow });

export interface BuildMonthSegmentsInput {
  /** Monday-anchored start of this week (for splitByWeek). */
  weekStart: Date;
  /** The 7 ISO day strings for this week, index = column. */
  weekISOs: string[];
  assignmentsByDate: Map<string, CalendarAssignmentRow[]>;
  tasksByDate: Map<string, CalendarTaskRow[]>;
  warrantyByDate: Map<string, CalendarWarrantyVisitRow[]>;
}

/** Build all month segments for one week. Jobs/estimates + warranty are
 *  multi-day spans (deduped by id, split to week-local cols); tasks are
 *  single-day (startCol === endCol). Emitted jobs/estimates first, then
 *  warranty, then tasks (the component partitions by kind to band-pack). */
export function buildMonthSegments(input: BuildMonthSegmentsInput): MonthSegment[] {
  const { weekStart, weekISOs } = input;
  const out: MonthSegment[] = [];

  // Assignments (install | estimate): dedupe across the week's days (a
  // multi-day row is fanned across each covered ISO key), then span.
  const seenA = new Map<string, CalendarAssignmentRow>();
  for (const iso of weekISOs)
    for (const a of input.assignmentsByDate.get(iso) ?? [])
      if (!seenA.has(a.id)) seenA.set(a.id, a);
  for (const a of seenA.values()) {
    for (const s of splitByWeek({
      rangeStart: a.assignedDateStart,
      rangeEnd: a.assignedDateEnd,
      weekStarts: [weekStart],
    })) {
      out.push({
        kind: a.purpose === 'estimate' ? 'estimate' : 'install',
        startCol: s.startCol,
        endCol: s.endCol,
        isFirstSegment: s.isFirstSegment,
        isLastSegment: s.isLastSegment,
        assignment: a,
      });
    }
  }

  // Warranty visits: dedupe + span exactly like assignments.
  const seenW = new Map<string, CalendarWarrantyVisitRow>();
  for (const iso of weekISOs)
    for (const v of input.warrantyByDate.get(iso) ?? [])
      if (!seenW.has(v.id)) seenW.set(v.id, v);
  for (const v of seenW.values()) {
    for (const s of splitByWeek({
      rangeStart: v.visitDateStart,
      rangeEnd: v.visitDateEnd,
      weekStarts: [weekStart],
    })) {
      out.push({
        kind: 'warranty',
        startCol: s.startCol,
        endCol: s.endCol,
        isFirstSegment: s.isFirstSegment,
        isLastSegment: s.isLastSegment,
        visit: v,
      });
    }
  }

  // Tasks: single-day → a one-column segment on that day.
  for (let col = 0; col < weekISOs.length; col++) {
    for (const t of input.tasksByDate.get(weekISOs[col]!) ?? []) {
      out.push({
        kind: 'task',
        startCol: col,
        endCol: col,
        isFirstSegment: true,
        isLastSegment: true,
        task: t,
      });
    }
  }

  return out;
}

/** True for the primary (job/estimate) band; used by the component to
 *  partition before band-packing. */
export function isJobSegment(s: MonthSegment): boolean {
  return s.kind === 'install' || s.kind === 'estimate';
}

/** Stable React key for a segment (kind + source row id). */
export function monthSegmentKey(s: MonthSegment): string {
  const id = s.kind === 'task' ? s.task.id : s.kind === 'warranty' ? s.visit.id : s.assignment.id;
  return `${s.kind}-${id}`;
}
